import { useEffect, useState } from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import TabList from "@mui/lab/TabList";
import Tab from "@mui/material/Tab";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import AlertBar from "../utils/AlertBar";
import { DataGrid } from "@mui/x-data-grid";
import { Grid } from "@mui/material";
import { Link } from "react-router-dom";

import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import { Avatar, Link as MuiLink, Button } from "@mui/material";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { getInitials } from "../../helpers/userUtils";
import { useTranslation } from "react-i18next";
import secureLocalStorage from "react-secure-storage";
import LanguageSwitcher from "../utils/LanguageSwitcher";
import { API_URL } from "../../config/environment";
import Badge from "@mui/material/Badge";
import { ActionsMenu } from "../utils/ActionsMenu";
import { HeadingTitle } from "../utils/HeadingTitle";
import EditRole from "../utils/EditRole";
import { hasAdminAccess, hasAdminOrEditorAccess } from "../../helpers/roles";
import CreateSheet from "../utils/CreateSheet";
import ImportIngredients from "../utils/ImportIngredients";
import ExportIngredients from "../utils/ExportIngredients";
import BulkUpdateIngredients from "../utils/BulkUpdateIngredients";
import BulkDeleteIngredients from "../utils/BulkDeleteIngredients";

import { useTitle } from "../utils/useTitle";

const drawerWidth = 175;

// styling
const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" }) (
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  })
);

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));


/**
 * Admin Dashboard Component
 * this component displays the admin dashboard:
 * - the Users page which displays every user and their status + role
 * - the Uploads page which allows you to modify the data, add data and delete data
 * 
 * @returns {JSX.Element} - Admin Dashboard Component
 */
export default function AdminDashboard() {
  useTitle("Admin Dashboard");
  const token = sessionStorage.getItem("authToken");
  const { t } = useTranslation();
  const navigate = useNavigate();
  const storedUser = secureLocalStorage.getItem("userInfo");
  const user = JSON.parse(storedUser);
  const initialTab = hasAdminAccess(user) ? "1" : "2";
  const theme = useTheme();

  const [open, setOpen] = useState(true);
  const [tab, setTab] = useState(initialTab);
  const [users, setUsers] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isEditRoleModalOpen, setIsEditRoleModalOpen] = useState(false);
  const [rowId, setRowId] = useState(null);
  const [isCreateSheetModalOpen, setIsCreateSheetModalOpen] = useState(false);
  const [isImportIngredientsModalOpen, setIsImportIngredientsModalOpen] = useState(false);
  const [isExportIngredientsModalOpen, setIsExportIngredientsModalOpen] = useState(false);
  const [isBulkUpdateIngredientsModalOpen, setIsBulkUpdateIngredientsModalOpen] = useState(false);
  const [isBulkDeleteIngredientsModalOpen,setIsBulkDeleteIngredientsModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleChangeTab = (event, newTab) => {
    setTab(newTab);
  };

  const getRowDataId = (id) => {
    setRowId(id);
  };

  const handleCloseEditRoleModal = () => {
    setIsEditRoleModalOpen(!isEditRoleModalOpen);
  };
  
  const columns = [
    {
      field: "firstName",
      headerName: t("admin-dashboard.users.table.label.firstName"),
      width: 300,
    },
    {
      field: "lastName",
      headerName: t("admin-dashboard.users.table.label.lastName"),
      width: 300,
    },
    {
      field: "email",
      headerName: t("admin-dashboard.users.table.label.email"),
      width: 300,
    },
    {
      field: "role",
      headerName: t("admin-dashboard.users.table.label.role"),
      width: 200,
      renderCell: (params) => {
        return (
          <RoleBadge role={params.value} />
        );
      },
    },
    {
      field: "subscribed",
      headerName: t("admin-dashboard.users.table.label.subscribed"),
      width: 200,
      renderCell: (params) => {
        return (
          <StatusBadge status={params.value} contentSuccess={"Subscribed"} contentError={"Not Subscribed"} />
        );
      },
    },
    {
      field: "activated",
      headerName: t("admin-dashboard.users.table.label.activated"),
      width: 200,
      renderCell: (params) => {
        return (
          <StatusBadge status={params.value} contentSuccess={"Activated"} contentError={"Deactivated"} />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      width: 90,
      renderCell: (params) => (
        <ActionsMenu
          rowData={params.row}
          onStatusChange={handleStatusChange}
          onSuccess={handleIsSuccessOpenState}
          onError={handleIsErrorOpenState}
          onModalChange={handleCloseEditRoleModal}
          onGetRowId={getRowDataId}
        />
      ),
    },
  ];

  function handleIsErrorOpenState(msg) {
    setIsError(true);
    setErrorMessage(msg);
  }

  function handleIsSuccessOpenState(msg) {
    setIsSuccess(true);
    setSuccessMessage(msg);
  }

  const handleCloseCreateSheetModal = () => {
    setIsCreateSheetModalOpen(!isCreateSheetModalOpen);
  };

  const handleCloseImportIngredientsModal = () => {
    setIsImportIngredientsModalOpen(!isImportIngredientsModalOpen);
  };

  const handleCloseExportIngredientsModal = () => {
    setIsExportIngredientsModalOpen(!isExportIngredientsModalOpen);
  };

  const handleCloseBulkUpdateModal = () => {
    setIsBulkUpdateIngredientsModalOpen(!isBulkUpdateIngredientsModalOpen);
  };
  const handleCloseBulkDeleteModal = () => {
    setIsBulkDeleteIngredientsModalOpen(!isBulkDeleteIngredientsModalOpen);
  };

  const getRowId = (row) => row._id; // Assuming `_id` is unique for each row

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleRedirectDashboard = () => {
    navigate("/dashboard");
    handleClose();
  };

  const handleLogout = () => {
    sessionStorage.removeItem("authToken");
    secureLocalStorage.clear();

    handleClose();

    navigate("/signin");
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  function handleIsErrorState() {
    setIsError(false);
  }

  function handleIsSuccessState() {
    setIsSuccess(false);
  }

  async function handlePaginationChange(page, offset) {
    setLoading(true);
    const __users = await getUsers();
    setUsers(__users);
    setLoading(false);
  }

  const handleStatusChange = async (updatedRows) => {
    const _users = await getUsers();
    if (_users) {
      setUsers(_users);
    }
  };

  async function getUsers(page = 0, offset = 1000) {
    try {
      const url = `${API_URL}/users?page=${page}&offset=${offset}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (data.status === "success") {
        const users = data.users;
        return users;
      } else {
        console.log("Could not fetch data from server");
        setIsError(true);
        setErrorMessage(data.message);
      }
    } catch (error) {
      console.log("Error processing request");
      setIsError(true);
      setErrorMessage("Error processing request");
    }
  }

  useEffect(() => {
    if (!token) {
      return navigate("/signin");
    }

    if (!hasAdminOrEditorAccess(user)) {
      return navigate("/dashboard");
    }
  }, []);

  useEffect(() => {
    if (!hasAdminAccess(user)) return;
    if (!loading) {
      return undefined;
    }

    (async () => {
      const _users = await getUsers();
      if (_users) {
        setUsers(_users);
      }
    })();

    setLoading(false);
  }, []);

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      
      <NavBar 
        t={t} 
        open={open} 
        handleDrawerOpen={handleDrawerOpen} 
        handleMenu={handleMenu} 
        user={user} 
        anchorEl={anchorEl} 
        handleClose={handleClose} 
        handleLogout={handleLogout} 
        handleRedirectDashboard={handleRedirectDashboard} 
      />
      
      <TabContext value={tab}>
        <SlidingNav open={open} handleDrawerClose={handleDrawerClose} theme={theme} handleChangeTab={handleChangeTab} user={user} />
        
        <UsersPage 
          user={user} 
          open={open} 
          getRowId={getRowId} 
          users={users} 
          loading={loading} 
          columns={columns} 
          pageSize={pageSize} 
          currentPage={currentPage} 
          setCurrentPage={setCurrentPage} 
          setPageSize={setPageSize} />    
    
        <UploadPage 
          t={t}
          open={open} 
          handleCloseCreateSheetModal={handleCloseCreateSheetModal} 
          handleCloseImportIngredientsModal={handleCloseImportIngredientsModal} 
          handleCloseExportIngredientsModal={handleCloseExportIngredientsModal} 
          handleCloseBulkUpdateModal={handleCloseBulkUpdateModal} 
          handleCloseBulkDeleteModal={handleCloseBulkDeleteModal}
        />
      </TabContext>

      <AlertBar
        open={isError}
        message={errorMessage}
        severity={"error"}
        onClose={handleIsErrorState}
      />

      <AlertBar
        open={isSuccess}
        message={successMessage}
        severity={"success"}
        onClose={handleIsSuccessState}
      />

      <EditRole
        open={isEditRoleModalOpen}
        rowId={rowId}
        onClose={handleCloseEditRoleModal}
        onSuccess={handleIsSuccessOpenState}
        onError={handleIsErrorOpenState}
        onStatusChange={handleStatusChange}
      />

      <CreateSheet
        open={isCreateSheetModalOpen}
        onClose={handleCloseCreateSheetModal}
      />

      <ImportIngredients
        open={isImportIngredientsModalOpen}
        onClose={handleCloseImportIngredientsModal}
      />

      <ExportIngredients
        open={isExportIngredientsModalOpen}
        onClose={handleCloseExportIngredientsModal}
      />

      <BulkUpdateIngredients
        open={isBulkUpdateIngredientsModalOpen}
        onClose={handleCloseBulkUpdateModal}
      />

      <BulkDeleteIngredients
        open={isBulkDeleteIngredientsModalOpen}
        onClose={handleCloseBulkDeleteModal}
      />
    </Box>
  );
}

/**
 * Role Badge Component
 * this component displays the badge with the role inscribed on it
 * 
 * @param {String} role 
 * @returns {JSX.Element} Role Badge Component
 */
function RoleBadge({ role }) {
  return (
    <Badge
      color={role === "admin" ? "primary" : "secondary"}
      badgeContent={role?.toUpperCase()}
      sx={{ marginLeft: "10px" }}
    >
      ""
    </Badge>
  );
}

/**
 * Status Badge Component
 * this component displays whether they are subscribed or activated and such!
 * 
 * @param {Boolean} status
 * @param {String} contentSuccess - Name if they are (ex. activated or subscribed)
 * @param {String} contentError - Name if they aren't
 * @returns Status Badge Component
 */
function StatusBadge({ status, contentSuccess, contentError }) {
  return (
    <Badge
      color={status ? "success" : "error"}
      badgeContent={status ? contentSuccess : contentError}
      sx={{ marginLeft: "40px" }}
    >
      ""
    </Badge>
  );
}

/**
 * Nav Bar Component
 * 
 * This component renders the top navigation bar
 * it shows the languages available and the menu when you press on the icon
 * 
 * @param {Function} t 
 * @param {Function} open 
 * @param {Function} handleDrawerOpen 
 * @param {Function} handleMenu 
 * @param {Object} user 
 * @param {Element} anchorEl 
 * @param {Function} handleClose 
 * @param {Function} handleLogout 
 * @param {Function} handleRedirectDashboard 
 * 
 * @returns {JSX.Element} Nav Bar Component
 */
function NavBar({ t, open, handleDrawerOpen, handleMenu, user, anchorEl, handleClose, handleLogout, handleRedirectDashboard }) {
  return (
    <AppBar position="fixed" open={open} color="success">
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          onClick={handleDrawerOpen}
          edge="start"
          sx={{ mr: 2, ...(open && { display: "none" }) }}
        >
          <MenuIcon />
        </IconButton>

        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          <MuiLink
            component={RouterLink}
            to="/dashboard"
            color="inherit"
            underline="none"
          >
            <img
              src="assets/logos/RitaWhite-2023Logo.png"
              alt="logo"
              style={{ width: "150px", height: "50px" }}
            />
          </MuiLink>
        </Typography>

        <LanguageSwitcher />

        <div>
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            color="inherit"
          >
            <Avatar>{getInitials(user?.firstName, user?.lastName)}</Avatar>
          </IconButton>

          <NavBarMenu t={t} anchorEl={anchorEl} handleClose={handleClose} handleRedirectDashboard={handleRedirectDashboard} handleLogout={handleLogout}/>
        </div>
      </Toolbar>
    </AppBar>
  );
}

/**
 * Nav Bar Menu Component
 * 
 * This component renders the dropdown menu that appears when the user clicks
 * on the profile icon. 
 * it shows the admin dashboard and the logout button
 * 
 * @param {Function} t 
 * @param {Element} anchorEl 
 * @param {Function} handleClose 
 * @param {Function} handleRedirectDashboard 
 * @param {Function} handleLogout 
 * 
 * @returns {JSX.Element} Nav Bar Menu Component
 */
function NavBarMenu({ t, anchorEl, handleClose, handleRedirectDashboard, handleLogout }) {
  return (
    <Menu
      id="menu-appbar"
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={Boolean(anchorEl)}
      onClose={handleClose}
    >
      <MenuItem onClick={handleRedirectDashboard}>
        {" "}
        {t("navigation.list.main-dashboard")}
      </MenuItem>
      <MenuItem onClick={handleLogout}>
        {" "}
        {t("navigation.list.logout")}
      </MenuItem>
    </Menu>
  );
}

/**
 * Sliding Navigation Component
 * 
 * This component renders the sliding nav on the left side of the page
 * You can press on:
 * Users to display the Users Page or
 * press on Uploads to display the Uploads Page
 * 
 * @param {Function} open 
 * @param {Function} handleDrawerClose 
 * @param {Object} theme 
 * @param {Function} handleChangeTab 
 * @param {Object} user 
 * 
 * @returns {JSX.Element} Sliding Navigation Component
 */
function SlidingNav({ open, handleDrawerClose, theme, handleChangeTab, user }) {
  return (
    <Drawer
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          backgroundColor: "#2e7d32",
        },
      }}
      variant="persistent"
      anchor="left"
      open={open}
      color="success"
    >
      <DrawerHeader>
        <IconButton onClick={handleDrawerClose}>
          {theme.direction === "ltr" ? (
            <ChevronLeftIcon />
          ) : (
            <ChevronRightIcon />
          )}
        </IconButton>
      </DrawerHeader>
      <Divider />
      <TabList
        onChange={handleChangeTab}
        aria-label="tab-label"
        orientation="vertical"
        variant="fullWidth"
      >
        {hasAdminAccess(user) && (
          <Tab
            icon={<PeopleAltIcon />}
            iconPosition="start"
            label="users"
            value="1"
            sx={{
              "&.Mui-selected": {
                backgroundColor: "transparent",
                color: "white", // Remove background color of the active tab
              },
            }}
          />
        )}

        <Tab
          icon={<CloudUploadIcon />}
          iconPosition="start"
          label="Uploads"
          value="2"
          sx={{
            "&.Mui-selected": {
              backgroundColor: "transparent",
              color: "white", // Remove background color of the active tab
            },
          }}
        />
      </TabList>
    </Drawer>
  );
}

/**
 * Users Page Component
 * this component displays the columns for the users
 * it shows:
 * the first name, last name, email, role, subscription status, activation status and actions
 * 
 * @param {Object} user
 * @param {Function} open
 * @param {Function} getRowId
 * @param {Object} users
 * @param {Boolean} loading
 * @param {Array} columns
 * @param {Number} pageSize
 * @param {Number} currentPage
 * @param {Function} setCurrentPage
 * @param {Function} setPageSize
 * 
 * @returns {JSX.Element} Users Page Component
 */
function UsersPage({ user, open, getRowId, users, loading, columns, pageSize, currentPage, setCurrentPage, setPageSize }) {
  return (
    <>
      {hasAdminAccess(user) && (
        <TabPanel value="1">
          <Main open={open}>
            <HeadingTitle text={"Users"} />
            <DataGrid
              autoHeight
              getRowId={getRowId}
              // getRowHeight={() => ""}
              rows={users || []}
              loading={loading}
              // rowCount={users?.total || 0}
              columns={columns}
              pagination
              pageSize={pageSize}
              pageSizeOptions={[10, 15, 20, 25, 50, 75, 100]}
              // paginationMode="server"
              paginationModel={{ page: currentPage, pageSize: pageSize }}
              onPaginationModelChange={(model) => {
                //   checkAccessLimit(model.page, model.pageSize);
                setCurrentPage(model.page);
                setPageSize(model.pageSize);
                // handlePaginationChange(model.page, model.pageSize);
              }}
              initialState={{
                pagination: {
                  paginationModel: {
                    page: 0,
                    pageSize: pageSize,
                  },
                },
              }}
            />
          </Main>
        </TabPanel>
      )}
    </>
  );
}

/**
 * Upload Page Component
 * This component renders the Upload Page
 * it allows the user to:
 * - Add new ingredients
 * - Update new ingredients
 * - Delete ingredients
 * 
 * @param {Function} t
 * @param {Function} open
 * @param {Function} handleCloseCreateSheetModal
 * @param {Function} handleCloseImportIngredientsModal
 * @param {Function} handleCloseExportIngredientsModal
 * @param {Function} handleCloseBulkUpdateModal
 * @param {Function} handleCloseBulkDeleteModal
 * 
 * @returns {JSX.Element} Upload Page Component
 */
function UploadPage({ t, open, handleCloseCreateSheetModal, handleCloseImportIngredientsModal, handleCloseExportIngredientsModal, handleCloseBulkUpdateModal, handleCloseBulkDeleteModal }) {
  return (
    <Main open={open}>
      <TabPanel value="2">
        <Grid
          container
          spacing={2}
          columns={12}
          style={{ marginBottom: "30px" }}
        >
          <UploadDescriptions 
            t={t} 
            descriptionType={"add-ing"} 
          />
          <UploadButtons 
            t={t}
            index={1}
            firstButtonFunc={handleCloseCreateSheetModal} 
            secondButtonFunc={handleCloseImportIngredientsModal}
          />

        </Grid>
        <hr></hr>
        <Grid
          container
          spacing={2}
          columns={12}
          style={{ marginBottom: "30px", marginTop: "50px" }}
        >
          <UploadDescriptions 
            t={t} 
            descriptionType={"update-ing"} 
          />
          <UploadButtons 
            t={t}
            index={3}
            firstButtonFunc={handleCloseExportIngredientsModal} 
            secondButtonFunc={handleCloseBulkUpdateModal}
          />
        </Grid>

        <hr></hr>
        <Grid
          container
          spacing={2}
          columns={12}
          style={{ marginBottom: "30px", marginTop: "50px" }}
        >
          <UploadDescriptions 
            t={t} 
            descriptionType={"delete-ing"} 
          />
          <UploadButtons 
            t={t}
            index={4}
            firstButtonFunc={handleCloseExportIngredientsModal} 
            secondButtonFunc={handleCloseBulkUpdateModal}
          />
        </Grid>
      </TabPanel>
    </Main>
  );
}

/**
 * Upload Descriptions Component
 * this component is a template for displaying the title and descriptions
 * 
 * @param {Function} t
 * @param {String} descriptionType
 * 
 * @returns {JSX.Element} Upload Descriptions Component
 */
function UploadDescriptions({ t, descriptionType }) {
  return (
    <>
      <HeadingTitle text={t(`admin-dashboard.uploads.${descriptionType}.title`)} />
      <Grid>
        <Typography paragraph>
          {t(`admin-dashboard.uploads.${descriptionType}.description-1`)}
        </Typography>
        <Typography paragraph>
          {t(`admin-dashboard.uploads.${descriptionType}.description-2`)}
        </Typography>
      </Grid>
    </>
  );
}

/**
 * Upload Buttons Component
 * This component displays is a template for displaying the two buttons side by side
 * 
 * @example
 * Create Sheet button with Import Ingredients
 * Export Ingredients with Update Ingredients
 * Export Ingredients with Delete Ingredients
 * 
 * @param {Function} t
 * @param {number} index
 * @param {Function} firstButtonFunc
 * @param {Function} secondButtonFunc
 * 
 * @returns {JSX.Element} Upload Buttons Component
 */
function UploadButtons({ t, index, firstButtonFunc, secondButtonFunc }) {
  const firstIndex = index;
  const secondIndex = index + 1;
  const isDelete = secondIndex === 5;

  return (
    <>
      <Button
        variant="contained"
        endIcon={<AddIcon />}
        color="success"
        component={Link}
        onClick={firstButtonFunc}
      >
        {isDelete ? t(`admin-dashboard.uploads.buttons.btn-${firstIndex - 1}`) : t(`admin-dashboard.uploads.buttons.btn-${firstIndex}`)}
      </Button>

      <Button
        variant="contained"
        endIcon={isDelete ? <DeleteIcon /> : <AddIcon />}
        color={isDelete ? "error" : "success"}
        component={Link}
        onClick={secondButtonFunc}
        style={{ marginLeft: 15 }}
      >
        {t(`admin-dashboard.uploads.buttons.btn-${secondIndex}`)}
      </Button>
    </>
  );
}